import { useState, useEffect, useCallback } from "react";
import { fetchModelPortfolio, fetchSheetPrices } from "./googleSheets";
import { detectCategory } from "./symbols";

/* ─── Helpers ────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return "—";
  // Zaten DD.MM.YYYY formatındaysa direkt dön
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(d)) return d;
  try { return new Date(d).toLocaleDateString("tr-TR"); }
  catch { return d; }
};

const fmtNum = (n, isTRY) => {
  if (n == null || isNaN(n)) return "—";
  const prefix = isTRY ? "₺" : "$";
  if (n >= 1000) return `${prefix}${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 1)    return `${prefix}${n.toFixed(2)}`;
  return `${prefix}${n.toPrecision(4)}`;
};

const resolveCat = (row) =>
  detectCategory(row.symbol) || "BIST";

/* ─── Component ──────────────────────────────────────────────── */
export default function ModelPortfolio({ t }) {
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Model portföy satırları + BIST canlı fiyatlarını paralel çek
      const [data, { prices: bistPrices }] = await Promise.all([
        fetchModelPortfolio(),
        fetchSheetPrices("BIST"),
      ]);

      // Her satır için: BIST sayfasındaki Fiyat (₺) → Güncel Fiyat
      // Bulunamazsa model portföy sayfasının D sütunu kullanılır
      const merged = data.map((row) => ({
        ...row,
        currentPrice: bistPrices[`${row.symbol}:BIST`] ?? row.currentPrice,
      }));

      setRows(merged);
      setLastUpdated(new Date());
    } catch (e) {
      setError("Veri yüklenemedi. Sayfanın 'Bağlantıya sahip herkes görüntüleyebilir' olduğundan emin olun.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Shared styles ── */
  const cardSt = {
    background: t.card, borderRadius: 20, padding: 24,
    border: `1px solid ${t.border}`,
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
    marginBottom: 20,
  };

  /* ── Summary ── */
  return (
    <div>
      {/* ── Header Card ── */}
      <div style={cardSt}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 700, color: t.muted, textTransform: "uppercase", letterSpacing: 1.5 }}>
              Model Portföy
            </p>
            <p style={{ margin: 0, fontSize: 13, color: t.muted }}>
              Sadece görüntüleme · Admin tarafından güncellenir
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {lastUpdated && (
              <span style={{ fontSize: 12, color: t.muted }}>
                {lastUpdated.toLocaleTimeString("tr-TR")}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              style={{
                padding: "9px 18px", borderRadius: 12, border: "none",
                background: "linear-gradient(135deg, #6366f1, #818cf8)",
                color: "#fff", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                fontSize: 13, display: "flex", alignItems: "center", gap: 7,
                opacity: loading ? 0.7 : 1,
                boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
              }}
            >
              {loading ? "⏳ Yükleniyor…" : "🔄 Yenile"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          ...cardSt, background: "#fef2f2",
          border: "1px solid #fecaca", color: "#dc2626",
          padding: "16px 20px",
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── Empty ── */}
      {!loading && !error && rows.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: t.muted }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>📭</div>
          <h2 style={{ margin: "0 0 8px", color: t.text }}>Henüz pozisyon yok</h2>
          <p style={{ margin: 0 }}>Admin sayfayı güncelledikten sonra burada görünür.</p>
        </div>
      )}

      {/* ── Table ── */}
      {rows.length > 0 && (
        <div style={cardSt}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {["Sembol", "Giriş Tarihi", "Maliyet", "Güncel Fiyat", "K / Z", "K / Z %", "Not"].map((h, i) => (
                  <th key={h} style={{
                    textAlign: i === 0 ? "left" : "right",
                    padding: "8px 10px",
                    color: t.muted, fontWeight: 700,
                    fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8,
                    borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const cat     = resolveCat(row);
                const isTRY   = cat === "BIST";
                const hasCurr = row.currentPrice != null;
                const pnlAmt  = hasCurr ? (row.currentPrice - row.cost) : null;
                const pnlPct  = hasCurr ? ((row.currentPrice - row.cost) / row.cost) * 100 : null;
                const isProfit = pnlAmt != null && pnlAmt >= 0;

                return (
                  <tr key={idx} style={{ borderBottom: `1px solid ${t.border}` }}>
                    {/* Sembol */}
                    <td style={{ padding: "12px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: "50%",
                          background: isTRY ? "#6366f1" : "#06b6d4",
                          flexShrink: 0,
                        }} />
                        <span style={{ fontWeight: 800, fontSize: 14 }}>{row.symbol}</span>
                        <span style={{
                          background: isTRY ? "#6366f122" : "#06b6d422",
                          color: isTRY ? "#6366f1" : "#06b6d4",
                          padding: "2px 7px", borderRadius: 20,
                          fontSize: 10, fontWeight: 700,
                        }}>{cat}</span>
                      </div>
                    </td>

                    {/* Giriş Tarihi */}
                    <td style={{ padding: "12px 10px", textAlign: "right", color: t.muted, fontSize: 12 }}>
                      {fmtDate(row.entryDate)}
                    </td>

                    {/* Maliyet */}
                    <td style={{ padding: "12px 10px", textAlign: "right", color: t.muted }}>
                      {fmtNum(row.cost, isTRY)}
                    </td>

                    {/* Güncel Fiyat */}
                    <td style={{ padding: "12px 10px", textAlign: "right" }}>
                      {hasCurr
                        ? <span style={{ fontWeight: 700 }}>{fmtNum(row.currentPrice, isTRY)}</span>
                        : <span style={{ color: t.muted, fontSize: 11 }}>—</span>
                      }
                    </td>

                    {/* K/Z Tutar */}
                    <td style={{ padding: "12px 10px", textAlign: "right" }}>
                      {pnlAmt != null ? (
                        <span style={{ fontWeight: 700, fontSize: 13, color: isProfit ? "#10b981" : "#ef4444" }}>
                          {isProfit ? "+" : ""}
                          {fmtNum(Math.abs(pnlAmt), isTRY)}
                        </span>
                      ) : <span style={{ color: t.muted, fontSize: 11 }}>—</span>}
                    </td>

                    {/* K/Z % */}
                    <td style={{ padding: "12px 10px", textAlign: "right" }}>
                      {pnlPct != null ? (
                        <span style={{
                          fontWeight: 800, fontSize: 13,
                          color: isProfit ? "#10b981" : "#ef4444",
                        }}>
                          {isProfit ? "▲" : "▼"}%{Math.abs(pnlPct).toFixed(1)}
                        </span>
                      ) : <span style={{ color: t.muted, fontSize: 11 }}>—</span>}
                    </td>

                    {/* Not */}
                    <td style={{ padding: "12px 10px", textAlign: "left", color: t.muted, fontSize: 12, maxWidth: 160 }}>
                      {row.notes || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
